import { productsAPI, ordersAPI } from "../../api";

// Résout dynamiquement un chemin vers un produit réel (le tour ne doit pas
// dépendre d'IDs de démo figés en dur, qui finiraient par ne plus exister).
const resolveOwnProductPath = async () => {
  try {
    const res = await productsAPI.getMyProducts();
    const product = res.data?.[0];
    return product ? `/products/${product.id}` : "/my-products";
  } catch {
    return "/my-products";
  }
};

const resolveBrowsableProductPath = async () => {
  try {
    const res = await productsAPI.getAll({});
    const product = res.data?.[0];
    return product ? `/products/${product.id}` : "/products";
  } catch {
    return "/products";
  }
};

const resolveOrderPath = async () => {
  try {
    const res = await ordersAPI.getAll({});
    const order = res.data?.[0];
    return order ? `/orders/${order.id}` : "/orders";
  } catch {
    return "/orders";
  }
};

export const BREEDER_TOUR_STEPS = [
  {
    id: "breeder-welcome",
    path: "/",
    target: "home-welcome",
    title: "Bienvenue sur PouletDirect !",
    text: "Je suis l'assistant PouletDirect, je vais vous montrer comment vendre vos produits directement aux restaurateurs, sans intermédiaire.",
  },
  {
    id: "breeder-home-cta",
    path: "/",
    target: "home-add-product",
    title: "Ajouter un produit",
    text: "Ce raccourci vous amène directement à la création d'une annonce depuis n'importe quelle page.",
  },
  {
    id: "breeder-my-products",
    path: "/my-products",
    target: "my-products-add",
    title: "Publier une annonce",
    text: "Poulets, œufs, volaille... Renseignez photos, prix et stock disponible. Vous pouvez modifier ou retirer une annonce à tout moment.",
  },
  {
    id: "breeder-product-detail",
    resolvePath: resolveOwnProductPath,
    target: ["product-detail", "my-products-add"],
    title: "Ce que voit le restaurateur",
    text: "Voici la fiche produit telle qu'un restaurateur la découvre : photos, prix, disponibilité et vos informations d'éleveur.",
  },
  {
    id: "breeder-orders",
    path: "/orders",
    target: "orders-list",
    title: "Recevoir des commandes",
    text: "Dès qu'un restaurateur commande, elle apparaît ici. Confirmez-la pour lancer la préparation.",
  },
  {
    id: "breeder-otp",
    resolvePath: resolveOrderPath,
    target: ["order-actions-breeder", "order-summary"],
    title: "Livraison protégée par code OTP",
    text: "Une fois la commande expédiée, un code OTP est envoyé au restaurateur. Le paiement ne vous est libéré qu'après qu'il l'ait validé — vous êtes protégé jusqu'à la remise.",
  },
  {
    id: "breeder-messages",
    path: "/messages",
    target: ["messages-chat-input", "messages-page"],
    title: "Échanger avec vos clients",
    text: "Répondez directement aux questions dans la messagerie intégrée. Pour votre sécurité, le partage de coordonnées personnelles y est bloqué.",
  },
  {
    id: "breeder-stats",
    path: "/stats",
    target: "stats-chart",
    title: "Suivre vos ventes",
    text: "Revenus par mois, commandes, produits les plus vendus : toutes vos statistiques en un coup d'œil.",
  },
  {
    id: "breeder-payment-info",
    path: "/profile",
    target: "profile-mobile-money",
    title: "Être payé",
    text: "Renseignez votre numéro Mobile Money ici : c'est là que vous recevez vos paiements après chaque livraison confirmée.",
  },
  {
    id: "breeder-end",
    path: "/",
    target: "navbar-settings-toggle",
    title: "C'est tout pour cette visite !",
    text: "Vous pouvez relancer cette visite guidée à tout moment depuis ce menu. Bonne vente sur PouletDirect !",
  },
];

export const RESTAURANT_TOUR_STEPS = [
  {
    id: "restaurant-welcome",
    path: "/",
    target: "home-welcome",
    title: "Bienvenue sur PouletDirect !",
    text: "Je suis l'assistant PouletDirect, je vais vous montrer comment commander vos produits directement auprès des éleveurs.",
  },
  {
    id: "restaurant-search",
    path: "/products",
    target: "products-filters",
    title: "Trouver vos éleveurs",
    text: "Filtrez par type de produit, ville ou prix pour trouver rapidement ce qu'il vous faut.",
  },
  {
    id: "restaurant-product-detail",
    resolvePath: resolveBrowsableProductPath,
    target: "product-detail",
    title: "La fiche produit",
    text: "Photos, prix, avis sur l'éleveur, disponibilité : tout ce qu'il faut pour décider en confiance.",
  },
  {
    id: "restaurant-order",
    resolvePath: resolveBrowsableProductPath,
    target: ["product-order-submit", "product-order-form", "product-detail"],
    title: "Passer commande",
    text: "Choisissez la quantité et votre mode de livraison — livraison par l'éleveur, par un tiers, ou point de rencontre.",
  },
  {
    id: "restaurant-payment",
    resolvePath: resolveOrderPath,
    target: ["order-payment", "order-summary"],
    title: "Paiement sécurisé",
    text: "Mobile Money ou carte bancaire, directement dans l'application. Le paiement est bloqué jusqu'à la confirmation de réception.",
  },
  {
    id: "restaurant-otp",
    resolvePath: resolveOrderPath,
    target: ["order-actions-restaurant", "order-summary"],
    title: "Confirmer la réception",
    text: "À la livraison, entrez le code OTP reçu par email/SMS pour confirmer et débloquer le paiement à l'éleveur.",
  },
  {
    id: "restaurant-messages",
    path: "/messages",
    target: ["messages-chat-input", "messages-page"],
    title: "Poser vos questions",
    text: "Échangez directement avec l'éleveur avant ou après votre commande, en toute sécurité.",
  },
  {
    id: "restaurant-review",
    resolvePath: resolveOrderPath,
    target: ["order-review", "order-summary"],
    title: "Laisser un avis",
    text: "Une fois la commande reçue, notez l'éleveur pour aider les autres restaurateurs à choisir en confiance.",
  },
  {
    id: "restaurant-stats",
    path: "/stats",
    target: "stats-chart",
    title: "Suivre vos achats",
    text: "Dépenses par mois et historique de commandes, pour garder un œil sur votre budget d'approvisionnement.",
  },
  {
    id: "restaurant-profile",
    path: "/profile",
    target: "profile-form",
    title: "Votre profil",
    text: "Gérez vos informations de restaurant et votre adresse de livraison par défaut.",
  },
  {
    id: "restaurant-end",
    path: "/",
    target: "navbar-settings-toggle",
    title: "C'est tout pour cette visite !",
    text: "Vous pouvez relancer cette visite guidée à tout moment depuis ce menu. Bonnes commandes sur PouletDirect !",
  },
];

export const getTourSteps = (role) =>
  role === "breeder" ? BREEDER_TOUR_STEPS : RESTAURANT_TOUR_STEPS;
